import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import tw from 'twin.macro'
import { TabsSelector } from 'src/components/shared/tabs-selector/TabsSelector'
import type { TabsSelectorOption } from 'src/components/shared/tabs-selector/TabsSelector.types'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { Typography } from '@mui/material'
import { SectionCardSkeleton } from 'src/components/shared/SectionCardSkeleton'
import { BusinessSetupStatusStrip } from 'src/components/customer/business/BusinessSetupStatusStrip'
import { HistoricalInvoicesCard } from 'src/components/customer/business/HistoricalInvoicesCard'
import { OatfiPaymentMethodCard } from 'src/components/customer/business/OatfiPaymentMethodCard'
import { PlaidLinkCard } from 'src/components/customer/business/PlaidLinkCard'
import { RequestUnderwritingModal } from 'src/components/customer/business/RequestUnderwritingModal'
import { SupplementalDataCard } from 'src/components/customer/business/SupplementalDataCard'
import { UnderwritingStatusCard } from 'src/components/customer/business/UnderwritingStatusCard'
import { BusinessProfileForm } from 'src/components/customer/business/business-profile-form/BusinessProfileForm'
import {
  AdminCreateOatfiPaymentMethod,
  AdminFetchOatfiBusiness,
  AdminFetchPlaidLinkToken,
  AdminRequestOatfiUnderwriting,
  AdminSaveOatfiBusiness,
  AdminUploadHistoricalInvoices,
  AdminUploadOatfiSupplementalData,
  ClearAdminOatfiPlaidLink,
} from 'src/constants/actionTypes'
import { UNDERWRITING_STATUS_LABELS } from 'src/constants/oatfi-business/constants'
import type { SupplementalDataType } from 'src/constants/oatfi-business/enums'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import type { OatfiBusinessFormFields } from 'src/interfaces/oatfiBusiness'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import { getCustomer } from 'src/selectors/customer'
import {
  getHistoricalInvoicesUploading,
  getOatfiBusiness,
  getOatfiBusinessError,
  getOatfiBusinessLoaded,
  getOatfiBusinessLoading,
  getOatfiBusinessPartnerId,
  getOatfiBusinessSaving,
  getOatfiPaymentMethodCreating,
  getOatfiSupplementalUploading,
  getOatfiUnderwritingRequesting,
  getPlaidLink,
  getPlaidLinkGenerating,
} from 'src/selectors/oatfiBusiness'
import { deriveSetupStatus } from 'src/utils/oatfiBusiness'

type BusinessTab = 'profile' | 'underwriting' | 'documents'

// Three topics an admin works on separately: the record the lender underwrites, the lender's
// decision and the two things it needs (bank link, payment method), and the documents sent
// to it. The setup strip above the tabs summarises all three at once.
const BUSINESS_TABS: TabsSelectorOption<BusinessTab>[] = [
  { label: 'Business profile', value: 'profile' },
  { label: 'Underwriting', value: 'underwriting' },
  { label: 'Documents', value: 'documents' },
]

// Documents holds nothing but the two upload cards. Without either upload permission it would be
// an empty tab, so it is not offered (ENG-1207 part 2).
const BUSINESS_TABS_WITHOUT_DOCUMENTS: TabsSelectorOption<BusinessTab>[] = BUSINESS_TABS.filter(
  (tab) => {
    return tab.value !== 'documents'
  },
)

const CustomerBusinessPageComponent = () => {
  const [selectedTab, setSelectedTab] = useState<BusinessTab>('profile')
  const { customerId } = useParams<{ customerId: string }>()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const dispatch = useDispatch()
  const snackbar = useSnackbar()

  const { isAuthenticated } = useAuthSession()
  const customer = useSelector(getCustomer)
  const business = useSelector(getOatfiBusiness)
  const isLoading = useSelector(getOatfiBusinessLoading)
  const isLoaded = useSelector(getOatfiBusinessLoaded)
  const loadedPartnerId = useSelector(getOatfiBusinessPartnerId)
  const error = useSelector(getOatfiBusinessError)
  const isSaving = useSelector(getOatfiBusinessSaving)
  const isRequestingUnderwriting = useSelector(getOatfiUnderwritingRequesting)
  const isUploadingSupplemental = useSelector(getOatfiSupplementalUploading)
  const isUploadingHistorical = useSelector(getHistoricalInvoicesUploading)
  const isCreatingPaymentMethod = useSelector(getOatfiPaymentMethodCreating)
  const plaidLink = useSelector(getPlaidLink)
  const isGeneratingPlaidLink = useSelector(getPlaidLinkGenerating)
  // One permission per endpoint family (spec §7.2): profile save, underwriting, payment method and
  // supplemental upload are `oatfi_manage`; Generate is `plaid_link_generate`; the historical
  // upload is `historical_invoices_upload`.
  const canManageBusiness = useHasPermission(InternalPermission.OatfiManage)
  const canGeneratePlaidLink = useHasPermission(InternalPermission.PlaidLinkGenerate)
  const canUploadHistoricalInvoices = useHasPermission(InternalPermission.HistoricalInvoicesUpload)
  const businessTabs =
    canManageBusiness || canUploadHistoricalInvoices
      ? BUSINESS_TABS
      : BUSINESS_TABS_WITHOUT_DOCUMENTS

  const [isConfirmingUnderwriting, setIsConfirmingUnderwriting] = useState<boolean>(false)
  // Bumped after a successful upload so the owning card clears its own fields.
  const [supplementalResetKey, setSupplementalResetKey] = useState<number>(0)
  const [historicalResetKey, setHistoricalResetKey] = useState<number>(0)

  const isReady = isLoaded && loadedPartnerId === customerId

  /**
   * The customer on screen right now, readable from inside an async callback.
   *
   * `customerId` captured in a mutation handler is the value from when the admin pressed the
   * button. If that mutation resolves after they have navigated to another customer, its
   * follow-up effects — the toast, the reset-key bump — no longer belong to the page.
   */
  const currentCustomerIdRef = useRef<string | undefined>(customerId)

  useEffect(() => {
    currentCustomerIdRef.current = customerId
  }, [customerId])

  const fetchBusiness = useCallback(() => {
    if (!customerId) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchOatfiBusiness.request({ partnerId: customerId }))
  }, [customerId, asyncDispatchWithErrorToast])

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    fetchBusiness()
  }, [isAuthenticated, fetchBusiness])

  /**
   * Local UI state must not survive a customer switch. React Router reuses this component
   * instance when only `:customerId` changes, so without this reset a confirm dialog opened for
   * the previous customer reappears under the new one — with its confirm wired to the new id.
   *
   * The Plaid link is cleared for a stronger reason: it is a customer-specific hosted URL, and
   * showing the previous customer's under a different name invites sending the wrong one. The
   * reducer clears it on a partner-changed fetch too; this covers the case where the fetch has
   * not fired yet.
   *
   * `ClearAdminOatfiPlaidLink` goes through a plain `dispatch`, not
   * `asyncDispatchWithErrorToast`: it is a plain `createAction` with no saga handler, so the
   * promise `createAsyncAction` returns would wait forever on an `action.next` nothing calls.
   * This mirrors how `CustomerWebhooksPage` dispatches `ClearAdminWebhookDeliveries`.
   */
  useEffect(() => {
    setIsConfirmingUnderwriting(false)
    dispatch(ClearAdminOatfiPlaidLink())
  }, [customerId, dispatch])

  const setupChips = useMemo(() => {
    return deriveSetupStatus(business)
  }, [business])

  const currentStatusLabel =
    business?.underwritingStatus === null || business?.underwritingStatus === undefined
      ? 'Not requested'
      : UNDERWRITING_STATUS_LABELS[business.underwritingStatus]

  const handleSaveProfile = useCallback(
    async (fields: OatfiBusinessFormFields) => {
      if (!customerId) {
        return
      }

      const mode = business === null ? 'create' : 'update'
      const result = await asyncDispatchWithErrorToast(
        AdminSaveOatfiBusiness.request({ partnerId: customerId, mode, fields }),
      )

      if (result === null || currentCustomerIdRef.current !== customerId) {
        return
      }

      snackbar?.enqueueSnackbar(
        mode === 'create' ? 'Business profile created' : 'Business profile saved',
        { variant: 'success' },
      )
    },
    [customerId, business, asyncDispatchWithErrorToast, snackbar],
  )

  const handleConfirmUnderwriting = useCallback(async () => {
    if (!customerId) {
      return
    }

    const result = await asyncDispatchWithErrorToast(
      AdminRequestOatfiUnderwriting.request({ partnerId: customerId }),
    )

    if (currentCustomerIdRef.current !== customerId) {
      return
    }

    setIsConfirmingUnderwriting(false)

    if (result === null) {
      return
    }

    snackbar?.enqueueSnackbar('Underwriting requested', { variant: 'success' })
  }, [customerId, asyncDispatchWithErrorToast, snackbar])

  const handleGeneratePlaidLink = useCallback(() => {
    if (!customerId) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchPlaidLinkToken.request({ partnerId: customerId }))
  }, [customerId, asyncDispatchWithErrorToast])

  const handleCopyPlaidLink = useCallback(
    (url: string) => {
      navigator.clipboard?.writeText(url)
      snackbar?.enqueueSnackbar('Bank-link URL copied', { variant: 'success' })
    },
    [snackbar],
  )

  const handleUploadSupplemental = useCallback(
    async (input: { type: SupplementalDataType; invoiceId?: string; file: File }) => {
      if (!customerId) {
        return
      }

      const result = await asyncDispatchWithErrorToast(
        AdminUploadOatfiSupplementalData.request({ partnerId: customerId, ...input }),
      )

      if (result === null || currentCustomerIdRef.current !== customerId) {
        return
      }

      setSupplementalResetKey((current) => {
        return current + 1
      })
      snackbar?.enqueueSnackbar('Document sent to the lender', { variant: 'success' })
    },
    [customerId, asyncDispatchWithErrorToast, snackbar],
  )

  const handleUploadHistorical = useCallback(
    async (file: File) => {
      if (!customerId) {
        return
      }

      const result = await asyncDispatchWithErrorToast(
        AdminUploadHistoricalInvoices.request({ partnerId: customerId, file }),
      )

      if (result === null || currentCustomerIdRef.current !== customerId) {
        return
      }

      setHistoricalResetKey((current) => {
        return current + 1
      })
      snackbar?.enqueueSnackbar('Historical invoices uploaded', { variant: 'success' })
    },
    [customerId, asyncDispatchWithErrorToast, snackbar],
  )

  const handleCreatePaymentMethod = useCallback(async () => {
    if (!customerId) {
      return
    }

    const result = await asyncDispatchWithErrorToast(
      AdminCreateOatfiPaymentMethod.request({ partnerId: customerId }),
    )

    if (result === null || currentCustomerIdRef.current !== customerId) {
      return
    }

    snackbar?.enqueueSnackbar('Oatfi payment method created', { variant: 'success' })
  }, [customerId, asyncDispatchWithErrorToast, snackbar])

  const handleOpenUnderwritingConfirm = useCallback(() => {
    setIsConfirmingUnderwriting(true)
  }, [])

  const handleCloseUnderwritingConfirm = useCallback(() => {
    setIsConfirmingUnderwriting(false)
  }, [])

  if (!isReady) {
    return (
      <div data-testid="admin-customer-business-page" css={tw`mx-auto w-full max-w-[720px] p-6`}>
        <Typography variant="h2" css={tw`mb-5`}>
          Business
        </Typography>
        {error === null ? (
          <SectionCardSkeleton fields={4} dataTestId="admin-customer-business-loading" />
        ) : (
          <Typography variant="body2" css={tw`text-shades-500`}>
            {error}
          </Typography>
        )}
      </div>
    )
  }

  return (
    <div data-testid="admin-customer-business-page" css={tw`mx-auto w-full max-w-[720px] p-6`}>
      <Typography variant="h2" css={tw`mb-5`}>
        Business
      </Typography>

      <div css={tw`flex flex-col gap-6`}>
        <BusinessSetupStatusStrip chips={setupChips} />

        <TabsSelector<BusinessTab>
          options={businessTabs}
          selectedValue={selectedTab}
          onSelect={setSelectedTab}
          testIdPrefix="admin-customer-business"
        />

        {selectedTab === 'profile' ? (
          <BusinessProfileForm
            business={business}
            isReadOnly={!canManageBusiness}
            isSaving={isSaving}
            onSubmit={handleSaveProfile}
          />
        ) : null}

        {selectedTab === 'underwriting' ? (
          <div css={tw`flex flex-col gap-6`}>
            <UnderwritingStatusCard
              business={business}
              canRequest={canManageBusiness}
              isRequesting={isRequestingUnderwriting}
              isRefreshing={isLoading}
              onRequest={handleOpenUnderwritingConfirm}
              onRefresh={fetchBusiness}
            />
            {/* The link only exists once generated, so without `plaid_link_generate` the card
                has nothing to show. */}
            {canGeneratePlaidLink ? (
              <PlaidLinkCard
                link={plaidLink}
                isGenerating={isGeneratingPlaidLink}
                onGenerate={handleGeneratePlaidLink}
                onCopy={handleCopyPlaidLink}
              />
            ) : null}
            <OatfiPaymentMethodCard
              business={business}
              canCreate={canManageBusiness}
              isCreating={isCreatingPaymentMethod}
              onCreate={handleCreatePaymentMethod}
            />
          </div>
        ) : null}

        {selectedTab === 'documents' ? (
          <div css={tw`flex flex-col gap-6`}>
            {canManageBusiness ? (
              <SupplementalDataCard
                resetKey={supplementalResetKey}
                isUploading={isUploadingSupplemental}
                onUpload={handleUploadSupplemental}
              />
            ) : null}
            {canUploadHistoricalInvoices ? (
              <HistoricalInvoicesCard
                resetKey={historicalResetKey}
                isUploading={isUploadingHistorical}
                onUpload={handleUploadHistorical}
              />
            ) : null}
          </div>
        ) : null}
      </div>

      <RequestUnderwritingModal
        isOpen={isConfirmingUnderwriting}
        customerName={customer?.name ?? 'this customer'}
        currentStatusLabel={currentStatusLabel}
        isRequesting={isRequestingUnderwriting}
        onConfirm={handleConfirmUnderwriting}
        onClose={handleCloseUnderwritingConfirm}
      />
    </div>
  )
}

export const CustomerBusinessPage = memo(CustomerBusinessPageComponent)
